<div #datepickerDropdownToggle>
  <button mat-stroked-button class="datepicker-stroked-toggle" (click)="toggleDropdown()" cdkOverlayOrigin>
    <ng-container *ngTemplateOutlet="buttonContent"></ng-container>
  </button>
  <button mat-icon-button class="datepicker-icon-toggle" (click)="toggleDropdown()" cdkOverlayOrigin>
    <ng-container *ngTemplateOutlet="buttonContent"></ng-container>
  </button>
</div>
<ng-template #buttonContent>
  <div class="button-content-wrapper">
    <div [class]="'date-container ' + dateContainerCollapseClass">
      @if (dateRangeDisplay) {
        @if (periodDisplay$ | async; as display) {
          <div class="selected-date-period-value">
            {{ display | translate }}
          </div>
        }
        <div class="selected-date-range-value">
          {{ dateRangeDisplay.range ?? '' | translate }}
        </div>
      } @else {
        <span>{{ 'GALAXY.DATE_RANGE.NO_PERIOD' | translate }}</span>
      }
    </div>
    <mat-icon aria-hidden="false" aria-label="Date range">date_range</mat-icon>
  </div>
</ng-template>
<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="datepickerDropdownToggle"
  [cdkConnectedOverlayOpen]="isOpen"
  [cdkConnectedOverlayPush]="true"
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayPositions]="_positions"
  [cdkConnectedOverlayBackdropClass]="showBackdrop() ? '' : 'cdk-overlay-transparent-backdrop'"
  (backdropClick)="onBackdropClick()"
>
  <mat-card class="date-range-container">
    <mat-card-content>
      <div class="selected-ranges-header">
        <div class="selected-date-range">
          <div class="date-range-title">
            {{ 'GALAXY.DATE_RANGE.SELECTED_DATE_RANGE' | translate }}
          </div>
          @if (dateRange && dateRange.start && dateRange.end) {
            <div>
              @if (includeToday()) {
                {{ dateRange.start | date }} - {{ dateRange.end | date }}
              } @else {
                {{ formatDateRange(dateRange) }}
              }
            </div>
          } @else {
            @if (isAllTimeSelected) {
              <span>{{ 'GALAXY.DATE_RANGE.PERIOD_SELECTOR.ALL_TIME' | translate }}</span>
            } @else {
              <span>{{ 'GALAXY.DATE_RANGE.NO_PERIOD' | translate }}</span>
            }
          }
        </div>

        @if (comparison()) {
          <div class="selected-date-range-comparison">
            <div class="date-range-title">
              {{ 'GALAXY.DATE_RANGE.COMPARE_TO' | translate }}
            </div>
            @if (comparisonDateRange; as comparison) {
              <div>
                @if (includeToday()) {
                  {{ comparison.start | date }} - {{ comparison.end | date }}
                } @else {
                  {{ formatDateRange(comparison) }}
                }
              </div>
            } @else {
              <span>{{ 'GALAXY.DATE_RANGE.NO_PERIOD' | translate }}</span>
            }
          </div>
        }
      </div>

      <mat-radio-group class="period-selector-container" [(ngModel)]="selectedPeriod">
        @for (period of filteredDateRangePeriods; track period.id) {
          <mat-radio-button
            class="period-select"
            color="primary"
            [value]="period.id"
            (change)="handleOnPeriodChange($event.value)"
          >
            {{ period.name | translate }}
          </mat-radio-button>
          @if (period.id === 7 && selectedPeriod() === 7) {
            <ng-template [ngTemplateOutlet]="monthSelect" />
          }
          @if (period.id === 8 && selectedPeriod() === 8) {
            <ng-template [ngTemplateOutlet]="quarterSelect" />
          }
          @if (period.id === 1 && selectedPeriod() === 1) {
            <ng-template [ngTemplateOutlet]="dateRangeSelect" />
          }
        }
      </mat-radio-group>
    </mat-card-content>

    <mat-card-actions class="date-range-selector-actions">
      <button mat-stroked-button (click)="closeDropdown()">
        {{ 'GALAXY.DATE_RANGE.CANCEL' | translate }}
      </button>
      <button mat-flat-button color="primary" (click)="handleOnApply()" [disabled]="!dateRange && !isAllTimeSelected">
        {{ 'GALAXY.DATE_RANGE.APPLY' | translate }}
      </button>
    </mat-card-actions>
  </mat-card>
</ng-template>

<ng-template #monthSelect>
  <div class="double-select-container">
    <glxy-form-field>
      <mat-select [formControl]="selectedMonthControl">
        @for (option of monthOptions; track option.value) {
          <mat-option [value]="option.value" [disabled]="option.disabled">
            {{ option.label | translate }}
          </mat-option>
        }
      </mat-select>
    </glxy-form-field>
    <glxy-form-field>
      <mat-select [formControl]="selectedMonthYearControl">
        @for (option of yearOptions(); track option.value) {
          <mat-option [value]="option.value" [disabled]="option.disabled">
            {{ option.label }}
          </mat-option>
        }
      </mat-select>
    </glxy-form-field>
  </div>
</ng-template>

<ng-template #quarterSelect>
  <div class="double-select-container">
    <glxy-form-field>
      <mat-select [formControl]="selectedQuarterControl">
        @for (option of quarterOptions; track option.value) {
          <mat-option [value]="option.value" [disabled]="option.disabled">
            {{ option.label | translate }}
          </mat-option>
        }
      </mat-select>
    </glxy-form-field>
    <glxy-form-field>
      <mat-select [formControl]="selectedQuarterYearControl">
        @for (option of yearOptions(); track option.value) {
          <mat-option [value]="option.value" [disabled]="option.disabled">
            {{ option.label }}
          </mat-option>
        }
      </mat-select>
    </glxy-form-field>
  </div>
</ng-template>

<ng-template #dateRangeSelect>
  <mat-form-field appearance="outline" class="date-range-select-field">
    <mat-date-range-input
      [rangePicker]="rangePicker"
      [max]="enableFuturePresetButtons() || allowFutureYearsInDropdown() ? null : today"
      [formGroup]="dateRangeFormGroup"
    >
      <input
        matStartDate
        formControlName="start"
        (dateChange)="onStartDateChange($event)"
        [placeholder]="'GALAXY.FILTER.CHIP.SELECT_START_PLACEHOLDER' | translate"
      />
      <input
        matEndDate
        formControlName="end"
        (dateChange)="onEndDateChange($event)"
        [placeholder]="'GALAXY.FILTER.CHIP.SELECT_END_PLACEHOLDER' | translate"
      />
    </mat-date-range-input>
    <mat-datepicker-toggle matSuffix [for]="rangePicker"></mat-datepicker-toggle>
    <mat-date-range-picker #rangePicker></mat-date-range-picker>
  </mat-form-field>
</ng-template>
